/**
 * pdp-explainers.js
 *
 * Behaviour for the apparel PDP explainer sections (sections/pdpx-*.liquid).
 * Every section renders complete without this file; it adds:
 *   - <pdpx-hotspots>  dots on a photo and a numbered list that open together
 *   - <pdpx-strip>     progress dashes and desktop arrows for sideways strips
 *   - <pdpx-video>     muted loops that play only while in view, with a pause
 *                      button. global.js pauseAllMedia() pauses every <video>
 *                      on the page and never resumes it, so this element owns
 *                      its own playback and resumes after an outside pause.
 *   - dataLayer events: pdp_explainer_view, pdp_hotspot_open,
 *                       pdp_fabric_swipe, pdp_ugc_shop
 *   - "Shop this look" sheets (snippets/pdpx-shop-sheet.liquid)
 *   - <pdpx-lengths>   re-fetches the lengths section after a colour switch
 *
 * Each section includes this script, so it guards against running twice.
 */
(function () {
  'use strict';

  if (window.__pdpExplainers) return;
  window.__pdpExplainers = true;

  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function track(event, data) {
    window.dataLayer = window.dataLayer || [];
    var payload = { event: event };
    for (var key in data) payload[key] = data[key];
    window.dataLayer.push(payload);
  }

  // One pdp_explainer_view per section, the first time it is half in view.
  var viewed = new WeakSet();
  var viewObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting || viewed.has(entry.target)) return;
          viewed.add(entry.target);
          viewObserver.unobserve(entry.target);
          track('pdp_explainer_view', { section: entry.target.getAttribute('data-pdpx-section') });
        });
      }, { threshold: 0.5 })
    : null;

  function observeSections(root) {
    if (!viewObserver) return;
    (root || document).querySelectorAll('[data-pdpx-section]').forEach(function (el) {
      if (!viewed.has(el)) viewObserver.observe(el);
    });
  }

  class PdpxHotspots extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.addEventListener('click', this.onClick.bind(this));
      var self = this;
      var open = this.querySelector('.pdpx-callout:not([hidden])');
      if (open) requestAnimationFrame(function () { self.place(open, open.getAttribute('data-point')); });
      window.addEventListener('resize', function () {
        var box = self.querySelector('.pdpx-callout:not([hidden])');
        if (box) self.place(box, box.getAttribute('data-point'));
      });
    }

    // Puts the text box beside its dot without covering another dot: tries
    // below and above, centred or running to either side, keeps the ones
    // inside the photo and takes the first that overlaps no other dot (or
    // the least overlap). The CSS position is the no-script fallback.
    place(box, n) {
      var view = box.parentElement;
      var dot = view.querySelector('.pdpx-dot[data-point="' + n + '"]');
      if (!dot) return;
      var area = view.getBoundingClientRect();
      if (!area.width) return;
      var d = dot.getBoundingClientRect();
      var cx = d.left + d.width / 2 - area.left;
      var cy = d.top + d.height / 2 - area.top;
      box.style.left = '0px';
      box.style.top = '0px';
      box.style.bottom = 'auto';
      var w = box.offsetWidth;
      var h = box.offsetHeight;
      var margin = 8;
      var reach = 26;
      var others = Array.prototype.filter.call(view.querySelectorAll('.pdpx-dot'), function (el) { return el !== dot; }).map(function (el) {
        var r = el.getBoundingClientRect();
        return { left: r.left - area.left + 5, top: r.top - area.top + 5, right: r.right - area.left - 5, bottom: r.bottom - area.top - 5 };
      });
      var tops = cy / area.height > 0.58 ? [cy - reach - h, cy + reach] : [cy + reach, cy - reach - h];
      var lefts = [cx - w / 2, cx - 22, cx - w + 22];
      var candidates = [];
      tops.forEach(function (top) {
        lefts.forEach(function (left) { candidates.push({ left: left, top: top }); });
      });
      // Beside the dot, vertically centred on it.
      var sideTop = Math.max(margin, Math.min(cy - h / 2, area.height - h - margin));
      candidates.push({ left: cx + reach, top: sideTop, side: true }, { left: cx - reach - w, top: sideTop, side: true });
      var best = null;
      candidates.forEach(function (c) {
        if (c.top < margin || c.top + h > area.height - margin) return;
        if (c.side && (c.left < margin || c.left + w > area.width - margin)) return;
        var left = Math.max(margin, Math.min(c.left, area.width - w - margin));
        var top = c.top;
        var hits = others.filter(function (o) { return o.left < left + w && o.right > left && o.top < top + h && o.bottom > top; }).length;
        if (!best || hits < best.hits) best = { left: left, top: top, hits: hits };
      });
      if (!best) {
        box.removeAttribute('style');
        box.style.setProperty('--x', dot.style.left);
        box.style.setProperty('--y', dot.style.top);
        return;
      }
      box.style.left = Math.round(best.left) + 'px';
      box.style.top = Math.round(best.top) + 'px';
      // Where a dot can't be avoided (small screens), it goes behind the box
      // so the text stays readable; tapping the box closes it.
      var placed = { left: best.left, top: best.top, right: best.left + w, bottom: best.top + h };
      Array.prototype.forEach.call(view.querySelectorAll('.pdpx-dot'), function (el) {
        if (el === dot) return el.classList.remove('is-covered');
        var r = el.getBoundingClientRect();
        var o = { left: r.left - area.left + 5, top: r.top - area.top + 5, right: r.right - area.left - 5, bottom: r.bottom - area.top - 5 };
        el.classList.toggle('is-covered', o.left < placed.right && o.right > placed.left && o.top < placed.bottom && o.bottom > placed.top);
      });
    }

    onClick(event) {
      // Tapping the open text box closes it.
      if (event.target.closest('.pdpx-callout')) {
        this.open(null);
        return;
      }
      var trigger = event.target.closest('[data-point]');
      if (!trigger || !this.contains(trigger)) return;
      var n = trigger.getAttribute('data-point');
      var row = this.querySelector('.pdpx-point[data-point="' + n + '"]');
      var alreadyOpen = row && row.classList.contains('is-open');
      // A second tap on the open point closes it.
      this.open(alreadyOpen ? null : n);
    }

    open(n) {
      var self = this;
      n = n === null || n === undefined ? null : String(n);
      var view = null;
      this.querySelectorAll('.pdpx-point').forEach(function (row) {
        var on = row.getAttribute('data-point') === n;
        row.classList.toggle('is-open', on);
        var btn = row.querySelector('.pdpx-point__btn');
        if (btn) btn.setAttribute('aria-expanded', on ? 'true' : 'false');
        var text = row.querySelector('.pdpx-point__text');
        if (text) text.hidden = !on;
        if (on) view = row.getAttribute('data-view');
      });
      if (view) {
        this.querySelectorAll('.pdpx-hotspots__view').forEach(function (el) {
          el.hidden = el.getAttribute('data-view') !== view;
        });
      }
      this.querySelectorAll('.pdpx-dot').forEach(function (dot) {
        dot.setAttribute('aria-pressed', dot.getAttribute('data-point') === n ? 'true' : 'false');
        dot.classList.remove('is-covered');
      });
      this.querySelectorAll('.pdpx-callout').forEach(function (box) {
        box.hidden = box.getAttribute('data-point') !== n;
        if (!box.hidden) self.place(box, n);
      });
      if (n) {
        var row = self.querySelector('.pdpx-point[data-point="' + n + '"]');
        track('pdp_hotspot_open', { n: Number(n), title: row ? row.getAttribute('data-title') : '' });
      }
    }
  }

  class PdpxStrip extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.track = this.querySelector('.pdpx-strip');
      if (!this.track) return;
      this.items = Array.prototype.slice.call(this.track.children);
      this.dashes = this.querySelectorAll('.pdpx__progress > span');
      this.prev = this.querySelector('[data-strip-prev]');
      this.next = this.querySelector('[data-strip-next]');
      this.arrows = this.querySelector('.pdpx__arrows');
      this.seen = {};
      var self = this;
      if (this.prev) this.prev.addEventListener('click', function () { self.step(-1); });
      if (this.next) this.next.addEventListener('click', function () { self.step(1); });
      this.setupColours();
      this.track.addEventListener('scroll', function () {
        if (self._raf) return;
        self._raf = requestAnimationFrame(function () {
          self._raf = null;
          self.update();
        });
      }, { passive: true });
      this.update();
    }

    // Creator strip colour chips: show one colour's tiles, or all of them,
    // up to the section's tile limit (data-max-tiles). The strip opens on All.
    setupColours() {
      var self = this;
      this.chipGroup = this.querySelector('[data-colour-chips]');
      if (!this.chipGroup) return;
      this.chipGroup.hidden = false;
      this.chipGroup.addEventListener('click', function (event) {
        var chip = event.target.closest('[data-colour-chip]');
        if (chip) self.showColour(chip.getAttribute('data-colour-chip'));
      });
    }

    showColour(colour) {
      var sizes = ['l', 's', 'm', 's'];
      var shown = 0;
      var max = Number(this.getAttribute('data-max-tiles') || 8);
      this.chipGroup.querySelectorAll('[data-colour-chip]').forEach(function (chip) {
        chip.setAttribute('aria-pressed', chip.getAttribute('data-colour-chip') === colour ? 'true' : 'false');
      });
      this.items.forEach(function (tile) {
        var on = (colour === 'all' || tile.getAttribute('data-colour') === colour) && shown < max;
        tile.hidden = !on;
        if (!on) return;
        // Keep the staggered rhythm across whichever tiles are showing.
        tile.classList.remove('pdpx-tile--l', 'pdpx-tile--m', 'pdpx-tile--s');
        tile.classList.add('pdpx-tile--' + sizes[shown % 4]);
        shown++;
      });
      this.track.scrollLeft = 0;
      this.update();
    }

    index() {
      var left = this.track.scrollLeft;
      var best = 0;
      var bestDistance = Infinity;
      var origin = this.items.length ? this.items[0].offsetLeft : 0;
      this.items.forEach(function (item, i) {
        var distance = Math.abs(item.offsetLeft - origin - left);
        if (distance < bestDistance) { bestDistance = distance; best = i; }
      });
      return best;
    }

    update() {
      var i = this.index();
      this.dashes.forEach(function (dash, k) { dash.classList.toggle('is-on', k === i); });
      var maxScroll = this.track.scrollWidth - this.track.clientWidth - 2;
      // Nothing to scroll (wide screens, few cards): no arrows to press.
      if (this.arrows) this.arrows.hidden = maxScroll <= 0;
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft >= maxScroll;
      var name = this.items[i] && this.items[i].getAttribute('data-fabric');
      if (i > 0 && name && !this.seen[name]) {
        this.seen[name] = true;
        track('pdp_fabric_swipe', { fabric: name });
      }
    }

    step(direction) {
      var i = Math.max(0, Math.min(this.items.length - 1, this.index() + direction));
      var origin = this.items[0].offsetLeft;
      this.track.scrollTo({ left: this.items[i].offsetLeft - origin, behavior: reducedMotion ? 'auto' : 'smooth' });
    }
  }

  class PdpxVideo extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.video = this.querySelector('video');
      this.button = this.querySelector('[data-video-toggle]');
      if (!this.video) return;
      this.userPaused = reducedMotion;
      this.inView = false;
      var self = this;
      if (this.button) {
        this.button.addEventListener('click', function () {
          self.userPaused = !self.video.paused;
          if (self.userPaused) self.video.pause();
          else self.play();
          self.sync();
        });
      }
      // pauseAllMedia() pauses us from outside; resume if we should be playing.
      this.video.addEventListener('pause', function () {
        setTimeout(function () {
          if (self.inView && !self.userPaused && self.video.paused) self.play();
          self.sync();
        }, 50);
      });
      this.video.addEventListener('play', function () { self.sync(); });
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          self.inView = entries[0].isIntersecting;
          if (self.inView && !self.userPaused) self.play();
          else self.video.pause();
        }, { threshold: 0.25 }).observe(this);
      }
      this.sync();
    }

    play() {
      if (this.video.preload === 'none') this.video.preload = 'metadata';
      var promise = this.video.play();
      if (promise && promise.catch) promise.catch(function () {});
    }

    sync() {
      if (!this.button) return;
      var playing = !this.video.paused;
      this.button.setAttribute('aria-pressed', playing ? 'false' : 'true');
      this.button.setAttribute('aria-label', playing ? this.button.getAttribute('data-label-pause') : this.button.getAttribute('data-label-play'));
    }
  }

  // Lengths link to the colour on screen. The colour switch re-renders only
  // main-product (and swaps #ProductData), so re-fetch this section for the
  // new product when #ProductData changes.
  class PdpxLengths extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      var data = document.getElementById('ProductData');
      if (!data || !('MutationObserver' in window)) return;
      var self = this;
      this._observer = new MutationObserver(function () { self.refresh(); });
      this._observer.observe(data, { childList: true, characterData: true, subtree: true });
    }

    disconnectedCallback() {
      if (this._observer) this._observer.disconnect();
    }

    refresh() {
      var handle = null;
      try { handle = JSON.parse(document.getElementById('ProductData').textContent).handle; } catch (e) {}
      if (!handle || handle === this.getAttribute('data-handle')) return;
      this.setAttribute('data-handle', handle);
      var section = this.closest('section');
      fetch('/products/' + handle + '?section_id=' + encodeURIComponent(this.getAttribute('data-section-id')))
        .then(function (response) { return response.ok ? response.text() : ''; })
        .then(function (html) {
          if (!section || !section.isConnected) return;
          var fresh = html && new DOMParser().parseFromString(html, 'text/html').querySelector('section.pdpx-lengths');
          if (fresh) section.replaceWith(fresh);
          else section.remove();
        })
        .catch(function () {});
    }
  }

  // "Shop this look" sheets: native <dialog>, opened by data-pdpx-sheet="<id>".
  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-pdpx-sheet]');
    if (opener) {
      var dialog = document.getElementById(opener.getAttribute('data-pdpx-sheet'));
      if (dialog && typeof dialog.showModal === 'function') {
        dialog.showModal();
        track('pdp_ugc_shop', { tile: opener.getAttribute('data-ugc-shop') || '' });
      }
      return;
    }
    var closer = event.target.closest('[data-pdpx-sheet-close]');
    if (closer) {
      closer.closest('dialog').close();
      return;
    }
    // A click on the backdrop lands on the dialog element itself.
    if (event.target.classList && event.target.classList.contains('pdpx-sheet')) event.target.close();
  });

  if (!customElements.get('pdpx-hotspots')) customElements.define('pdpx-hotspots', PdpxHotspots);
  if (!customElements.get('pdpx-strip')) customElements.define('pdpx-strip', PdpxStrip);
  if (!customElements.get('pdpx-video')) customElements.define('pdpx-video', PdpxVideo);
  if (!customElements.get('pdpx-lengths')) customElements.define('pdpx-lengths', PdpxLengths);

  observeSections();
  // Sections re-rendered by the theme editor come back without observers.
  document.addEventListener('shopify:section:load', function (event) { observeSections(event.target); });
})();
